import {
  IconAdjustmentsHorizontal,
  IconCoin,
  IconContract,
  IconCreditCard,
  IconFlame,
  IconHeart,
  IconLifebuoy,
  IconSend,
  IconSettings,
  IconStar,
  IconUsersGroup,
} from '@tabler/icons-react-native';
import { type userInfoAtom } from 'app/atoms';
import type { Icon } from 'app/design/icons';
import { AmbassadorMedalIcon } from 'app/design/icons/ambassador-medal-icon';
import { SuperRibbon } from 'app/design/icons/super-ribbon';
import { useTrackStreakFlameClick } from 'app/lib/streak-analytics';
import type * as profile from 'app/screens/profile/navigator';
import type * as root from 'app/screens/root-stack';
import { AmplifyAuthConfiguration } from 'app/services/amplify';
import { WhiteLabel } from 'app/types/white-labels';
import { canUseInterests, FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { useUISize } from 'app/utils/web-helpers';
import { Amplify } from 'aws-amplify';
import { type useAtomValue } from 'jotai/index';
import type React from 'react';
import { useAmbassador } from './use-ambassador';

Amplify.configure({ Auth: { Cognito: AmplifyAuthConfiguration } });

interface UserProfilSections {
  userInfo: ReturnType<typeof useAtomValue<typeof userInfoAtom>>;
  navigate: (route: keyof root.Parameters | keyof profile.Parameters, screen?: string, params?: object) => void;
  t: (key: string) => string;
  groups: { id: string }[] | undefined;
}

interface ProfileLink {
  Prefix: Icon;
  label: string;
  routeName: string;
  onPress: () => void;
}

interface ProfileSection {
  name: string;
  title: string;
  links: ProfileLink[];
}

export const useProfileSections = ({ userInfo, navigate, t, groups }: UserProfilSections) => {
  const { isSmallUI } = useUISize();
  const { canBecomeAmbassador, isAmbassador } = useAmbassador();
  const trackStreakFlameClick = useTrackStreakFlameClick();

  const { canMeasureImpact, canBecomeSuperSharer, canListMembers, canViewSubscription, canStreak } = {
    canListMembers: isFeatureEnabled(FeatureFlag.listMembers, userInfo?.featureFlags),
    canMeasureImpact: isFeatureEnabled(FeatureFlag.measureImpact, userInfo?.featureFlags),
    canBecomeSuperSharer: isFeatureEnabled(FeatureFlag.superSharer, userInfo?.featureFlags),
    canViewSubscription: isFeatureEnabled(FeatureFlag.viewSubscription, userInfo?.featureFlags),
    canStreak: isFeatureEnabled(FeatureFlag.streak, userInfo?.featureFlags),
  };

  const createOtherSection = (): ProfileSection => {
    const links: ProfileLink[] = [];

    // Lien "Inviter des voisins"
    if (isFeatureEnabled(FeatureFlag.invite)) {
      links.push({
        label: 'inviteNeighboursButtonLabel',
        routeName: 'invite',
        Prefix: IconSend,
        onPress: () => navigate('invite'),
      });
    }

    // Lien "Nous contacter"
    links.push({
      label: 'contactUsButtonLabel',
      Prefix: IconLifebuoy,
      routeName: 'contact-us',
      onPress: () => navigate('contact-us'),
    });

    links.push({
      label: 'tosButtonLabel',
      Prefix: IconContract,
      routeName: 'privacy',
      onPress: () => navigate('privacy'),
    });

    return {
      name: 'other',
      title: t('otherTitle'),
      links,
    };
  };

  const createMyImpactSection = (): ProfileSection | null => {
    const links: ProfileLink[] = [];

    if (canBecomeAmbassador || isAmbassador) {
      links.push({
        label: isAmbassador ? 'ambassadorButtonLabel' : 'ambassadorButtonLabelNotYet',
        Prefix: AmbassadorMedalIcon,
        routeName: 'ambassador',
        onPress: () => navigate('ambassador'),
      });
    }

    if (canStreak) {
      links.push({
        label: 'streak',
        Prefix: IconFlame,
        routeName: 'streak',
        onPress: () => {
          trackStreakFlameClick();
          navigate('streak');
        },
      });
    }

    if (canBecomeSuperSharer) {
      links.push({
        label: userInfo?.isSuper ? 'superButtonLabel' : 'superButtonLabelNotYet',
        Prefix: SuperRibbon,
        routeName: 'super-partageur',
        onPress: () => navigate('super-partageur'),
      });
    }

    if (canMeasureImpact) {
      links.push({
        label: 'myFinancialImpactButtonLabel',
        Prefix: IconCoin,
        routeName: 'my-impact-financial',
        onPress: () => navigate('my-impact-financial'),
      });
    }

    if (!isWhiteLabel(WhiteLabel.Mutuo)) {
      links.push({
        label: 'myRatingsButtonLabel',
        Prefix: IconStar,
        routeName: 'my-ratings',
        onPress: () => navigate('my-ratings'),
      });
    }

    return {
      name: 'myImpact',
      title: t('myImpactTitle'),
      links,
    };
  };

  const createPersonalInformationSection = (): ProfileSection => {
    const links: ProfileLink[] = [];

    if (isSmallUI) {
      links.push({
        label: 'myInformationButtonLabel',
        Prefix: IconSettings,
        routeName: 'my-information',
        onPress: () => navigate('my-information'),
      });
    }

    if (isFeatureEnabled(FeatureFlag.justOneClub)) {
      if (canListMembers) {
        links.push({
          label: 'myClubButtonLabel',
          routeName: 'group-members',
          Prefix: IconUsersGroup,
          onPress: () => navigate('group-members', undefined, { groupIds: groups?.map((g) => g.id) }),
        });
      }
    } else {
      links.push({
        label: 'myClubsButtonLabel',
        Prefix: IconUsersGroup,
        routeName: 'my-clubs',
        onPress: () => navigate('my-clubs'),
      });
    }

    if (canUseInterests()) {
      links.push({
        label: 'myInterestsButtonLabel',
        Prefix: IconHeart,
        routeName: 'my-interests',
        onPress: () => navigate('my-interests'),
      });
    }

    links.push({
      label: 'myPreferencesButtonLabel',
      Prefix: IconAdjustmentsHorizontal,
      routeName: 'my-preferences',
      onPress: () => navigate('my-preferences'),
    });

    if (canViewSubscription) {
      links.push({
        label: 'mySubscriptionButtonLabel',
        Prefix: IconCreditCard,
        routeName: 'my-plan',
        onPress: () => navigate('my-plan'),
      });
    }

    return {
      name: 'personalInformation',
      title: t('myInformationTitle'),
      links,
    };
  };

  const sections = (() => {
    const sectionsArray: (ProfileSection | React.JSX.Element)[] = [];

    const myImpactSection = createMyImpactSection();
    if (myImpactSection) {
      sectionsArray.push(myImpactSection);
    }

    sectionsArray.push(createPersonalInformationSection());
    sectionsArray.push(createOtherSection());

    return sectionsArray;
  })();

  return { sections };
};
